import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChild,
    ViewChildren
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ApplicationFacade, PurchaseService } from '@ridango/components';
import { ActivatedRoute, Router } from '@angular/router';
import Env from '@ridango/core/env';
import {
    NextButtonElement,
    ProductArea,
    ProductAreaType,
    Translation
} from '@ridango/components/lib/purchase/purchase.model';
import { takeUntil, take } from 'rxjs/operators';
import { BaseComponent } from '@ridango/core';
import { timer } from 'rxjs';
import { TicketDetailsZoneMapComponent } from '@ridango/widgets';
import { ModalController } from '@ionic/angular';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';
import { AreaImageComponent } from './area-image/area-image.component';

const currentStep = 'product-area';

@Component({
    selector: 'gl-purchase-v2-single-or-two-area',
    templateUrl: './single-or-two-area.component.html',
    styleUrls: ['./single-or-two-area.component.scss']
})
export class SingleOrTwoAreaComponent extends BaseComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    public productAreaType: ProductAreaType = ProductAreaType.ONE_AREA;
    public AreasCountTypes = ProductAreaType;
    public areaOptions = {
        [ProductAreaType.ONE_AREA]: [],
        [ProductAreaType.TWO_AREAS]: [],
        [ProductAreaType.COUNTY]: null
    };
    public bigButtons = false;
    public selectedArea = null;
    public twoAreas;
    public assetsPath$ = this.applicationFacade.assetsPath$;
    currentLanguage = this.translate.currentLang;
    private readonly VIEW_ZONE_MAP = 'View zone map';
    private readonly CHOOSE_WHERE_TO = 'Choose where to travel';

    @ViewChildren('areas') areasQueryList: QueryList<ElementRef>;
    @ViewChild(AreaImageComponent) areaImageComponent: AreaImageComponent;
    @Output() nextButtonElement = new EventEmitter<NextButtonElement>();
    @Input() productAreas: ProductArea[];

    @Input() set selectedProductAreas(value: any) {
        const selectedAreaId = this.normalizeAreaId(value?.selectedProductAreas?.[0]);
        this.selectedArea = this.productAreas.find((areaItem) => this.normalizeAreaId(areaItem?.id) === selectedAreaId);
        this.nextButtonElement.emit({
            content: { textTerm: 'BUTTON_NEXT' },
            disabled: !this.selectedArea
        });
    }

    constructor(
        @Inject('ENV') public env: Env,
        private readonly translate: TranslateService,
        private readonly activatedRoute: ActivatedRoute,
        private readonly modalCtrl: ModalController,
        private readonly applicationFacade: ApplicationFacade,
        private readonly router: Router,
        private readonly cdr: ChangeDetectorRef,
        private readonly purchaseService: PurchaseService,
        private readonly analyticsService: AnalyticsService
    ) {
        super();
    }

    ngOnInit(): void {
        this.purchaseService.setPurchaseDataLoading(false);
        this.translate.onLangChange.pipe(takeUntil(this.componentDestroyed$)).subscribe((event) => {
            this.currentLanguage = event.lang;
        });

        this.twoAreas = !!this.env.PURCHASE_FLOW?.SECOND_PRODUCT_AREA_TAB;
    }

    ngAfterViewInit() {
        this.initPage();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.productAreas) {
            this.areaOptions[ProductAreaType.ONE_AREA] = this.productAreas.filter(
                (area) => area.code === ProductAreaType.ONE_AREA
            );
            this.areaOptions[ProductAreaType.TWO_AREAS] = this.productAreas.filter(
                (area) => area.code === ProductAreaType.TWO_AREAS
            );
            if (this.env.PURCHASE_FLOW?.SHOW_COUNTY_AREA_GRID) {
                this.areaOptions[ProductAreaType.COUNTY] = this.productAreas.filter(
                    (area) => area.code === ProductAreaType.COUNTY
                );
            } else {
                this.areaOptions[ProductAreaType.COUNTY] = this.productAreas.find(
                    (area) => area.code === ProductAreaType.COUNTY
                );
            }

            const urlSegments = this.activatedRoute.snapshot.url.map((segment) => segment.path);
            const lastSegment = urlSegments[urlSegments.length - 1];
            const normalizedLastSegment = this.normalizeAreaId(lastSegment);

            if (lastSegment !== currentStep) {
                if (this.normalizeAreaId(this.areaOptions[ProductAreaType.COUNTY]?.id) === normalizedLastSegment) {
                    this.selectedArea = this.areaOptions[ProductAreaType.COUNTY];
                    this.productAreaType = ProductAreaType.COUNTY;
                } else {
                    this.selectedArea = this.areaOptions[ProductAreaType.ONE_AREA].find(
                        (a) => this.normalizeAreaId(a?.id) === normalizedLastSegment
                    );
                }
            }
            if (!this.env.PURCHASE_FLOW?.USE_SMALL_BUTTONS) {
                this.bigButtons = !!this.areaOptions[ProductAreaType.ONE_AREA].find(
                    (area) =>
                        area.name?.translations?.find((n) => n.lang === this.currentLanguage)?.translation?.length > 2
                );
            }
        }
    }

    get isCountryArea(): boolean {
        return this.env.PURCHASE_FLOW?.HIDE_MAP && this.productAreaType === this.AreasCountTypes.COUNTY;
    }

    get shouldShowAreaGrid(): boolean {
        return this.productAreaType !== ProductAreaType.COUNTY || !!this.env.PURCHASE_FLOW?.SHOW_COUNTY_AREA_GRID;
    }

    get currentAreaOptions(): ProductArea[] {
        const options = this.areaOptions[this.productAreaType];

        if (Array.isArray(options)) {
            return options;
        }

        return options ? [options] : [];
    }

    ngOnDestroy(): void {
        this.nextButtonElement.emit(null);
        super.ngOnDestroy();
    }

    areaSelected(area, event?) {
        const currentUrlSegments = this.activatedRoute.snapshot.url.map((segment) => segment.path);
        const urlSegments = [...currentUrlSegments];
        const normalizedSelectedAreaId = this.normalizeAreaId(area?.id);
        this.trackSelectedArea(area);
        urlSegments.splice(urlSegments.indexOf(currentStep) + 1);

        const currentSelectedAreaId = this.normalizeAreaId(currentUrlSegments[urlSegments.indexOf(currentStep) + 1]);

        this.selectedArea = area ?? null;
        if (currentSelectedAreaId === normalizedSelectedAreaId) {
            this.nextButtonElement.emit({
                content: { textTerm: 'BUTTON_NEXT' },
                disabled: !this.selectedArea
            });
            return;
        }

        if (this.selectedArea) {
            this.router.navigate([this.translate.currentLang, 'purchase-v2', ...urlSegments, normalizedSelectedAreaId]);
        } else {
            this.router.navigate([this.translate.currentLang, 'purchase-v2', ...urlSegments]);
        }
        this.nextButtonElement.emit({
            content: { textTerm: 'BUTTON_NEXT' },
            disabled: !this.selectedArea
        });

        if (event) {
            timer(10).subscribe(() => {
                event.target.parentElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
            });
        }
    }

    private resolveLocalizedMapSrc(assetsPath: string): string {
        const supportedLocales = ['sv', 'en'];
        const locale = supportedLocales.includes(this.currentLanguage) ? this.currentLanguage : 'en';
        return `${assetsPath}/img/purchase-flow-v2/product-area/areas-map-${locale}.png`;
    }

    async openZoneMap() {
        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.PURCHASE_FLOW,
            label: this.VIEW_ZONE_MAP
        });
        const assetsPath = await this.applicationFacade.assetsPath$.pipe(take(1)).toPromise();
        const modal = await this.modalCtrl.create({
            component: TicketDetailsZoneMapComponent,
            componentProps: {
                imageSrc: this.areaImageComponent?.imageSrc || null,
                localizedMapSrc: this.resolveLocalizedMapSrc(assetsPath)
            },
            cssClass: 'ticket-details-zone-map-modal'
        });
        const content = document.querySelector('ion-content');

        await modal.present().then(() => {
            if (content) {
                content.setAttribute('inert', '');
            }
        });
        await modal.onDidDismiss().then(() => {
            if (content) {
                content.removeAttribute('inert');
            }
        });
    }

    private initPage() {
        const urlSegments = this.activatedRoute.snapshot.url.map((segment) => segment.path);
        const lastSegment = urlSegments[urlSegments.length - 1];
        const normalizedLastSegment = this.normalizeAreaId(lastSegment);

        if (lastSegment !== currentStep) {
            const oneAreaSelected = this.areaOptions[this.productAreaType].find(
                (a) => this.normalizeAreaId(a?.id) === normalizedLastSegment
            );

            if (oneAreaSelected) {
                this.selectedArea = oneAreaSelected;
                this.scrollToItemById(this.selectedArea.id);
            } else {
                this.selectedArea = this.areaOptions[ProductAreaType.COUNTY];
                this.productAreaType = ProductAreaType.COUNTY;
            }

            this.cdr.detectChanges();
        }
        this.nextButtonElement.emit({
            content: { textTerm: 'BUTTON_NEXT' },
            disabled: !this.selectedArea
        });
    }

    private scrollToItemById(itemId: string): void {
        const itemToScroll = this.areasQueryList.find((item) => item.nativeElement.id === itemId);

        if (itemToScroll) {
            timer(500)
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe(() => {
                    itemToScroll.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                });
        }
    }

    private trackSelectedArea(area) {
        if (!area) return;

        const lang = (area.name?.translations as Array<Translation>)?.find((item) => item.lang === 'en');
        this.analyticsService.eventTrack(TrackedEventAction.SELECTED, {
            category: TrackedEventCategory.PURCHASE_FLOW,
            name: `${this.CHOOSE_WHERE_TO} - ${lang?.translation}`
        });
    }

    private normalizeAreaId(value: string | number | null | undefined): string | null {
        if (value === null || value === undefined) {
            return null;
        }

        return String(value);
    }
}
